import type { GrammarExercise } from '../exercise-types';

export const foundationsExercises: Record<string, GrammarExercise[]> = {
  'verb-be-affirmative-negative': [
    {
      kind: 'multiple-choice',
      prompt: 'I ___ a confident English learner.',
      options: ['am', 'is', 'are'],
      correctIndex: 0,
      explanation: 'With I, the verb be is always AM: I am → I’m.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Simone ___ the CEO of her own company.',
      options: ['are', 'is', 'am'],
      correctIndex: 1,
      explanation: 'With she, he, and it, we use IS: She is → She’s.',
    },
    {
      kind: 'fill-blank',
      prompt: 'We ___ in class today, practicing together.',
      answers: ['are', '’re', "'re"],
      explanation: 'With we, you, and they, we use ARE: We are → We’re.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'They ___ afraid of making mistakes — mistakes are part of learning!',
      options: ['aren’t', 'isn’t', 'am not'],
      correctIndex: 0,
      explanation: 'The negative of are is are not → aren’t.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Fernanda ___ 42 years old.',
      answers: ['is', '’s', "'s"],
      explanation: 'In English, we use the verb be to indicate age — we do NOT say “she has 42 years old”.',
    },
    {
      kind: 'matching',
      instruction: 'Match each subject with the correct form of the verb be:',
      pairs: [
        { left: 'I', right: 'am' },
        { left: 'She', right: 'is' },
        { left: 'They', right: 'are' },
      ],
    },
  ],

  'glossary-of-grammar-terms': [
    {
      kind: 'multiple-choice',
      prompt: 'In the sentence “Maria studies English”, the word Maria is the ___.',
      options: ['subject', 'verb', 'object'],
      correctIndex: 0,
      explanation: 'The subject is the person or thing that does the action in a sentence.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'In the sentence “She reads a book”, the words a book are the ___.',
      options: ['subject', 'object', 'verb'],
      correctIndex: 1,
      explanation: 'The object is the person or thing that receives the action of the verb.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which word is a gerund?',
      options: ['Swimming', 'To swim', 'Swims'],
      correctIndex: 0,
      explanation: 'The gerund is the -ing form of a verb used as a noun: Swimming is good exercise.',
    },
    {
      kind: 'fill-blank',
      prompt: 'A verb form that begins with to, like in “I need to study German”, is called the ___.',
      answers: ['infinitive'],
      explanation: 'The infinitive is the form of a verb that begins with to.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“She is studying for her certification right now.” Which verb form is this?',
      options: ['continuous', 'perfect', 'simple'],
      correctIndex: 0,
      explanation: 'The continuous form (be + verb + -ing) describes actions happening now or around the present moment.',
    },
    {
      kind: 'matching',
      instruction: 'Match each grammar term with its example:',
      pairs: [
        { left: 'Gerund', right: 'Swimming is good exercise.' },
        { left: 'Infinitive', right: 'I need to study German.' },
        { left: 'Perfect', right: 'She has finished her work.' },
        { left: 'Simple', right: 'She works every day.' },
      ],
      explanation: 'Knowing these terms helps you understand every explanation in this material.',
    },
  ],

  'cognate-words': [
    {
      kind: 'multiple-choice',
      prompt: 'Information, education, communication — how do we pronounce the ending -tion?',
      options: ['/shon/', '/jon/', '/ti/'],
      correctIndex: 0,
      explanation: 'Words ending in -tion are pronounced /shon/: situation, information, education.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Famous, generous, ambitious — which ending do these cognates share?',
      options: ['-ous', '-ive', '-ble'],
      correctIndex: 0,
      explanation: 'Words ending in -ous, pronounced /us/, are cognates: religious, famous, generous, ambitious.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which English ending usually matches Portuguese words like “cidade” and “universidade”?',
      options: ['-ty', '-tion', '-ble'],
      correctIndex: 0,
      explanation: 'Words ending in -ty, pronounced /ti/ or /ri/, are cognates: city, university, honesty.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Complete the cognate: in English, “universidade” is ___.',
      answers: ['university'],
      explanation: 'Portuguese -dade becomes English -ty: university, city, simplicity.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Complete the cognate: in English, “impossível” is ___.',
      answers: ['impossible'],
      explanation: 'Words ending in -ble, pronounced /bou/, are cognates: possible, impossible, responsible.',
    },
    {
      kind: 'matching',
      instruction: 'Match each word ending with a cognate that uses it:',
      pairs: [
        { left: '-tion', right: 'information' },
        { left: '-ous', right: 'famous' },
        { left: '-ble', right: 'responsible' },
        { left: '-ist', right: 'feminist' },
      ],
      explanation: 'Recognizing cognates means you already know thousands of English words!',
    },
  ],

  'word-classification': [
    {
      kind: 'multiple-choice',
      prompt: 'In “Ana is a brilliant engineer”, the word brilliant is a(n) ___.',
      options: ['noun', 'adjective', 'adverb'],
      correctIndex: 1,
      explanation: 'An adjective gives us more information about people, animals or things represented by nouns.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'In “She swims really well”, the word well is a(n) ___.',
      options: ['adverb', 'adjective', 'noun'],
      correctIndex: 0,
      explanation: 'Adverbs add more information about a verb — here, well tells us how she swims.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'In “We ought to book a trip to Salvador soon”, the word book is a ___.',
      options: ['noun', 'verb', 'adjective'],
      correctIndex: 1,
      explanation: 'Many words belong to more than one class: book can be a noun (an interesting book) or a verb (to book a trip).',
    },
    {
      kind: 'multiple-choice',
      prompt: '“He loves fast cars” — fast is an adjective. In “Don’t drive so fast!”, fast is a(n) ___.',
      options: ['adjective', 'noun', 'adverb'],
      correctIndex: 2,
      explanation: 'Fast can be an adjective (fast cars) or an adverb (drive fast) — the class depends on how the word is used.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Words like and and but, which link one word, phrase or clause to another, are called ___.',
      answers: ['conjunctions'],
      explanation: 'Conjunctions show a link between words, phrases or clauses: Joe and Dan are brothers.',
    },
    {
      kind: 'fill-blank',
      prompt: 'A word that refers to a person, animal or thing, like woman or house, is a ___.',
      answers: ['noun'],
      explanation: 'Nouns carry a lot of meaning and are the most common type of word in English.',
    },
    {
      kind: 'matching',
      instruction: 'Match each word class with an example:',
      pairs: [
        { left: 'Noun', right: 'meeting' },
        { left: 'Verb', right: 'negotiate' },
        { left: 'Adjective', right: 'confident' },
        { left: 'Adverb', right: 'carefully' },
        { left: 'Preposition', right: 'of' },
      ],
    },
  ],

  'question-words': [
    {
      kind: 'multiple-choice',
      prompt: '“___ is your new manager?” “Her name is Patricia.”',
      options: ['Who', 'What', 'Where'],
      correctIndex: 0,
      explanation: 'Who is used to ask about a person.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“___ did you decide to study English?” “Because I want to work abroad!”',
      options: ['When', 'Why', 'Who'],
      correctIndex: 1,
      explanation: 'Why asks for a reason — the answer often starts with because.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“___ do you go to work?” “I go by bus.”',
      options: ['How', 'Where', 'Whose'],
      correctIndex: 0,
      explanation: 'How asks about a method.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“___ of these two dresses do you prefer for the interview, the red one or the blue one?”',
      options: ['What', 'Which', 'Whose'],
      correctIndex: 1,
      explanation: 'We use which when the person needs to make a choice between a few options.',
    },
    {
      kind: 'fill-blank',
      prompt: '“___ is the meeting?” “It’s tomorrow at 10 a.m.”',
      answers: ['when'],
      explanation: 'When asks about a time.',
    },
    {
      kind: 'fill-blank',
      prompt: '“___ did you spend your vacation?” “In Bahia, with my best friend!”',
      answers: ['where'],
      explanation: 'Where asks about a place.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“___ laptop is this?” “It’s mine!”',
      options: ['Whose', 'Who', 'Which'],
      correctIndex: 0,
      explanation: 'Whose asks about possession.',
    },
    {
      kind: 'matching',
      instruction: 'Match each question word with what it asks about:',
      pairs: [
        { left: 'Who', right: 'a person' },
        { left: 'Where', right: 'a place' },
        { left: 'When', right: 'a time' },
        { left: 'Why', right: 'a reason' },
        { left: 'How', right: 'a method' },
      ],
    },
    {
      kind: 'multiple-choice',
      prompt: 'Question words start open questions. What does that mean?',
      options: [
        'The answer is never just “yes” or “no”.',
        'The answer is always “yes”.',
        'The question doesn’t need an answer.',
      ],
      correctIndex: 0,
      explanation: 'Open questions begin with a question word, so the answer will never be “yes” or “no”.',
    },
  ],

  'verb-be-question-form': [
    {
      kind: 'multiple-choice',
      prompt: 'She is from Brazil. → How do we turn this into a question?',
      options: ['Is she from Brazil?', 'She is from Brazil?', 'Does she be from Brazil?'],
      correctIndex: 0,
      explanation: 'To make a question with the verb be, we invert the verb and the subject: She is → Is she?',
    },
    {
      kind: 'multiple-choice',
      prompt: '___ you ready for your presentation?',
      options: ['Is', 'Are', 'Am'],
      correctIndex: 1,
      explanation: 'With you, we use are — and in questions it comes before the subject: Are you?',
    },
    {
      kind: 'fill-blank',
      prompt: '___ Beyoncé a great singer? — Yes, she is!',
      answers: ['is'],
      explanation: 'With she, he, and it, the question starts with Is + subject.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which question is correct?',
      options: ['Where she is from?', 'Where is she from?', 'Where from she is?'],
      correctIndex: 1,
      explanation: 'With a question word, the order is question word + verb be + subject: Where is she from?',
    },
    {
      kind: 'fill-blank',
      prompt: 'How ___ you? — I’m great, thank you!',
      answers: ['are'],
      explanation: 'The question word comes first, followed by the verb be and then the subject.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'What ___ your profession? — I am a teacher.',
      options: ['is', 'are', 'am'],
      correctIndex: 0,
      explanation: 'The subject is your profession (it), so we use is after the question word.',
    },
  ],

  'simple-present': [
    {
      kind: 'multiple-choice',
      prompt: 'Luiza ___ English every day before work.',
      options: ['studies', 'study', 'studys'],
      correctIndex: 0,
      explanation: 'Verbs ending in y preceded by a consonant take -ies in the third person: study → studies.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'He ___ TV only on weekends.',
      options: ['watchs', 'watches', 'watch'],
      correctIndex: 1,
      explanation: 'Verbs ending in o, ch, sh, th, ss, gh or z take -es in the third person: watch → watches.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Marina ___ (go) to the gym on Mondays.',
      answers: ['goes'],
      explanation: 'Go ends in o, so the third person takes -es: she goes.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'She ___ breakfast at 8 am before her first client.',
      options: ['have', 'has', 'haves'],
      correctIndex: 1,
      explanation: 'The verb have is irregular: for he, she, and it we use HAS.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'They ___ work on Sundays — rest is important too!',
      options: ['don’t', 'doesn’t', 'aren’t'],
      correctIndex: 0,
      explanation: 'For I, you, we, and they, the negative uses the auxiliary do: do + not = don’t.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: ['She doesn’t likes coffee.', 'She doesn’t like coffee.', 'She don’t like coffee.'],
      correctIndex: 1,
      explanation: 'In the negative we do NOT conjugate the main verb — the auxiliary doesn’t is already conjugated.',
    },
    {
      kind: 'fill-blank',
      prompt: '___ she have breakfast? — No, she has brunch!',
      answers: ['does'],
      explanation: 'For he, she, and it, questions in the simple present start with the auxiliary Does.',
    },
    {
      kind: 'matching',
      instruction: 'Match each verb with its third person singular form (he, she, it):',
      pairs: [
        { left: 'watch', right: 'watches' },
        { left: 'study', right: 'studies' },
        { left: 'play', right: 'plays' },
        { left: 'work', right: 'works' },
      ],
      explanation: 'The ending depends on how the verb finishes: -es, -ies, or just -s.',
    },
  ],

  'adverbs-of-frequency': [
    {
      kind: 'multiple-choice',
      prompt: 'I ___ have lunch at home — every single day, 100% of the time.',
      options: ['always', 'sometimes', 'never'],
      correctIndex: 0,
      explanation: 'Always means 100% of the time.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Paula ___ goes to the beach — 0% of the time; she prefers the mountains.',
      options: ['usually', 'often', 'never'],
      correctIndex: 2,
      explanation: 'Never means 0% of the time.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'She is ___ late to work — about 70% of the time, because of the traffic.',
      options: ['often', 'rarely', 'never'],
      correctIndex: 0,
      explanation: 'Often indicates something happens around 70% of the time.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I ___ ever go to the movies — maybe twice a year.',
      answers: ['hardly'],
      explanation: 'Hardly ever, like rarely, means something happens only about 5% of the time.',
    },
    {
      kind: 'fill-blank',
      prompt: 'How ___ do you work out? — Twice a week.',
      answers: ['often'],
      explanation: 'We ask about frequency with How often…?',
    },
    {
      kind: 'matching',
      instruction: 'Match each frequency with its adverb:',
      pairs: [
        { left: '100%', right: 'always' },
        { left: '90%', right: 'usually' },
        { left: '50%', right: 'sometimes' },
        { left: '5%', right: 'rarely' },
        { left: '0%', right: 'never' },
      ],
      explanation: 'Adverbs of frequency indicate how often something happens, from always to never.',
    },
  ],

  'pronouns-overview': [
    {
      kind: 'multiple-choice',
      prompt: 'I admire Elza Soares. ___ was an incredible singer.',
      options: ['She', 'Her', 'Herself'],
      correctIndex: 0,
      explanation: 'Subject pronouns replace nouns that are the subject of the clause: Elza Soares → she.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'My mentor helped me so much, so I wrote ___ a thank-you letter.',
      options: ['she', 'her', 'herself'],
      correctIndex: 1,
      explanation: 'Object pronouns replace nouns that are the object of the verb: I wrote her a letter.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Do you usually cut ___ while cooking?',
      options: ['you', 'yourself', 'yours'],
      correctIndex: 1,
      explanation: 'Reflexive pronouns refer back to the subject when the subject and the object are the same person.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'She ___ designed her graduation dress — no help needed!',
      options: ['her', 'she', 'herself'],
      correctIndex: 2,
      explanation: 'Reflexive pronouns can be intensive pronouns to emphasize that you did something by yourself.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Ana organized the whole conference alone — we are so proud of ___. (Ana)',
      answers: ['her'],
      explanation: 'After a verb or preposition, we use the object pronoun: proud of her.',
    },
    {
      kind: 'fill-blank',
      prompt: 'My sisters and I started a book club. ___ meet every month.',
      answers: ['we'],
      explanation: 'We is the subject pronoun that replaces “my sisters and I”.',
    },
    {
      kind: 'matching',
      instruction: 'Match each subject pronoun with its object pronoun:',
      pairs: [
        { left: 'I', right: 'me' },
        { left: 'she', right: 'her' },
        { left: 'we', right: 'us' },
        { left: 'they', right: 'them' },
      ],
      explanation: 'The pronoun changes depending on its function: subject or object of the sentence.',
    },
  ],

  'possessive-pronouns-vs-adjectives': [
    {
      kind: 'multiple-choice',
      prompt: 'This is ___ house — I bought it with my own salary!',
      options: ['my', 'mine', 'me'],
      correctIndex: 0,
      explanation: 'A possessive adjective is always followed by a noun: my house.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'That house is ___ — I bought it with my own salary!',
      options: ['my', 'mine', 'me'],
      correctIndex: 1,
      explanation: 'A possessive pronoun is used without a noun — it replaces the noun: That house is mine (my house).',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Your presentation was great, but the committee liked ___ even more. (her presentation)',
      options: ['her', 'hers', 'she'],
      correctIndex: 1,
      explanation: 'Hers replaces “her presentation” — no noun comes after a possessive pronoun.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The students are doing ___ homework.',
      options: ['their', 'theirs', 'them'],
      correctIndex: 0,
      explanation: 'Before a noun like homework, we need the possessive adjective their.',
    },
    {
      kind: 'fill-blank',
      prompt: 'This is my passport. Where is ___? (your passport)',
      answers: ['yours'],
      explanation: 'Yours replaces “your passport” so we don’t repeat the noun.',
    },
    {
      kind: 'fill-blank',
      prompt: '“Whose phone is this?” “It’s ___!” (my phone)',
      answers: ['mine'],
      explanation: 'Mine is the possessive pronoun that replaces “my phone”.',
    },
    {
      kind: 'matching',
      instruction: 'Match each possessive adjective with its possessive pronoun:',
      pairs: [
        { left: 'my', right: 'mine' },
        { left: 'your', right: 'yours' },
        { left: 'her', right: 'hers' },
        { left: 'our', right: 'ours' },
        { left: 'their', right: 'theirs' },
      ],
      explanation: 'The adjective comes before a noun; the pronoun stands alone and replaces it.',
    },
  ],

  'relative-pronouns': [
    {
      kind: 'multiple-choice',
      prompt: 'Maria is the lawyer ___ won the case.',
      options: ['who', 'which', 'whose'],
      correctIndex: 0,
      explanation: 'Who is used mainly for people and replaces subject pronouns like she.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Bridget visited the park with her dog, ___ likes to chase squirrels.',
      options: ['who', 'which', 'whom'],
      correctIndex: 1,
      explanation: 'Which is used for objects and animals.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'This is the woman ___ company grew 200% last year.',
      options: ['whose', 'who', 'which'],
      correctIndex: 0,
      explanation: 'Whose indicates possession: the woman’s company.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The office ___ I work is in this building.',
      options: ['where', 'which', 'who'],
      correctIndex: 0,
      explanation: 'Where can be a relative pronoun when it introduces clauses that refer to a place.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She is the teacher to ___ I asked a question.',
      answers: ['whom'],
      explanation: 'Whom has the same meaning as who, but appears after prepositions.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She remembered the day ___ they met.',
      answers: ['when'],
      explanation: 'When can be a relative pronoun when it introduces clauses that describe time.',
    },
    {
      kind: 'matching',
      instruction: 'Match each relative pronoun with its use:',
      pairs: [
        { left: 'who', right: 'people' },
        { left: 'which', right: 'objects and animals' },
        { left: 'whose', right: 'possession' },
        { left: 'where', right: 'a place' },
        { left: 'when', right: 'a time' },
      ],
    },
  ],

  'omission-of-that': [
    {
      kind: 'multiple-choice',
      prompt: 'In which sentence can that be left out?',
      options: [
        'She said that she was busy.',
        'She replied that she wasn’t interested.',
        'He shouted that he was coming.',
      ],
      correctIndex: 0,
      explanation: 'That can be left out after common reporting verbs like said — but NOT after reply or shout.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“This is the woman that we were talking about.” Which sentence is also correct?',
      options: [
        'This is the woman we were talking about.',
        'This is woman we were talking about.',
        'This is the woman whom we talking about.',
      ],
      correctIndex: 0,
      explanation: 'The relative pronoun that can be left out when it is the object in a relative clause.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'He shouted he was coming.',
        'I’m glad you are safe.',
        'She replied she wasn’t interested.',
      ],
      correctIndex: 1,
      explanation: 'That can be left out after adjectives like glad — but never after verbs like reply and shout.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“She suggested we should wait.” Is this sentence correct?',
      options: [
        'Yes — that can be left out after suggested.',
        'No — that is always necessary after reporting verbs.',
        'No — suggested cannot introduce a clause.',
      ],
      correctIndex: 0,
      explanation: 'Suggested is a common reporting verb, so that can be left out after it.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She said ___ she was busy. (the word we can also leave out here)',
      answers: ['that'],
      explanation: 'After said, both versions are correct: She said that she was busy / She said she was busy.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'After which verb can that NOT be dropped?',
      options: ['reply', 'say', 'think'],
      correctIndex: 0,
      explanation: 'That cannot be dropped after verbs like reply and shout.',
    },
  ],

  'demonstrative-adjectives': [
    {
      kind: 'multiple-choice',
      prompt: 'You are holding one book in your hands: “___ book is fascinating!”',
      options: ['This', 'These', 'That'],
      correctIndex: 0,
      explanation: 'This is for one thing that is near you — like pointing at something in your hands.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'You see some houses far away on the hill: “___ houses are beautiful!”',
      options: ['These', 'Those', 'This'],
      correctIndex: 1,
      explanation: 'Those is for plural things that are far from you.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Your friend points to a café across the street: “Let’s try ___ café!”',
      options: ['this', 'these', 'that'],
      correctIndex: 2,
      explanation: 'That is for one thing that is far from you.',
    },
    {
      kind: 'fill-blank',
      prompt: '___ shoes here next to me are perfect for the interview. (plural, near)',
      answers: ['these'],
      explanation: 'These is for plural things that are near you.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Do you remember ___ day last year when you gave your first speech in English? (singular, far in time)',
      answers: ['that'],
      explanation: 'Near and far can be in time too — that refers to a moment far in the past.',
    },
    {
      kind: 'matching',
      instruction: 'Match each demonstrative with its use:',
      pairs: [
        { left: 'this', right: 'singular, near' },
        { left: 'that', right: 'singular, far' },
        { left: 'these', right: 'plural, near' },
        { left: 'those', right: 'plural, far' },
      ],
      explanation: 'Using a demonstrative is equivalent to pointing at something or someone.',
    },
  ],

  'there-is-there-are': [
    {
      kind: 'multiple-choice',
      prompt: '___ a new café near my office — perfect for studying English!',
      options: ['There is', 'There are', 'There am'],
      correctIndex: 0,
      explanation: 'There is should be used when the noun that follows is in the singular: a café.',
    },
    {
      kind: 'multiple-choice',
      prompt: '___ many talented women at the conference today.',
      options: ['There is', 'There are', 'There be'],
      correctIndex: 1,
      explanation: 'There are should be used when the noun that follows is in the plural: many women.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'There ___ a cat in my living room.',
      options: ['are', 'is', 'am'],
      correctIndex: 1,
      explanation: 'A cat is singular, so we use there is.',
    },
    {
      kind: 'fill-blank',
      prompt: 'There ___ a lot of amazing women in sports.',
      answers: ['are'],
      explanation: 'The noun that follows is plural (women), so we use there are.',
    },
    {
      kind: 'fill-blank',
      prompt: 'There ___ a kitchen, a living room, and a bedroom in my apartment.',
      answers: ['is'],
      explanation: 'The noun right after the verb is singular (a kitchen), so we use there is.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: ['There are a meeting at 9.', 'There is a meeting at 9.', 'There is meetings at 9.'],
      correctIndex: 1,
      explanation: 'There is + singular noun (a meeting); there are + plural noun (meetings).',
    },
    {
      kind: 'fill-blank',
      prompt: 'There ___ two free workshops for women entrepreneurs this Saturday.',
      answers: ['are'],
      explanation: 'Two workshops is plural, so we use there are.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“There is three books on the table.” Is this correct?',
      options: [
        'No — books is plural, so we need there are.',
        'Yes — there is works with any noun.',
        'No — it should be there am.',
      ],
      correctIndex: 0,
      explanation: 'There is and there are indicate existence — the choice depends on the noun being singular or plural.',
    },
  ],

  'prepositions-of-place': [
    {
      kind: 'multiple-choice',
      prompt: 'Camila has a meeting ___ New York next week.',
      options: ['in', 'on', 'at'],
      correctIndex: 0,
      explanation: 'We use in for cities, states, and countries: in New York, in Japan.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Your contract is ___ the table, ready to sign!',
      options: ['in', 'on', 'at'],
      correctIndex: 1,
      explanation: 'We use on for a surface: on the table, on the wall.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Jane is waiting for you ___ the bus stop.',
      options: ['in', 'on', 'at'],
      correctIndex: 2,
      explanation: 'We use at for a point — usually specific places: at the bus stop, at the door.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The keys are ___ my pocket.',
      options: ['in', 'on', 'at'],
      correctIndex: 0,
      explanation: 'We use in for an enclosed space: in my pocket, in a box.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Her new office is ___ the 7th floor.',
      answers: ['on'],
      explanation: 'Floors follow the surface rule: on the 7th floor.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She is ___ work until 6 — then she goes to her English class.',
      answers: ['at'],
      explanation: 'At is used in fixed expressions like at work, at home, at school.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which is correct?',
      options: ['on a bus', 'in a bus', 'at a bus'],
      correctIndex: 0,
      explanation: 'By convention, we say on a bus, on a train, on a plane — but in a car, in a taxi.',
    },
    {
      kind: 'matching',
      instruction: 'Match each preposition with its general rule:',
      pairs: [
        { left: 'in', right: 'an enclosed space' },
        { left: 'on', right: 'a surface' },
        { left: 'at', right: 'a point' },
      ],
      explanation: 'There are many exceptions, but these general rules help you choose.',
    },
  ],

  'prepositions-of-time': [
    {
      kind: 'multiple-choice',
      prompt: 'The team meeting is ___ Monday.',
      options: ['in', 'on', 'at'],
      correctIndex: 1,
      explanation: 'We usually use on with days and dates: on Fridays, on 19 October.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I get up ___ 6.30 a.m. and go for a run.',
      options: ['in', 'on', 'at'],
      correctIndex: 2,
      explanation: 'We usually use at with clock times: at 6.30 a.m., at lunchtime.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Bianca started her own business ___ 2024.',
      options: ['in', 'on', 'at'],
      correctIndex: 0,
      explanation: 'We use in with longer periods of time such as months, seasons, and years.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'She’s a nurse and she works ___ night.',
      options: ['in', 'on', 'at'],
      correctIndex: 2,
      explanation: 'We say at night for all of the night — but in the night for a specific time during the night.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I usually relax ___ the evening, after my English lesson.',
      answers: ['in'],
      explanation: 'We use in with parts of the day: in the morning, in the evening.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Her birthday is ___ 19 October.',
      answers: ['on'],
      explanation: 'Dates take on: on 19 October, on January 18th.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The weather is very cold here ___ Christmas.',
      options: ['in', 'on', 'at'],
      correctIndex: 2,
      explanation: 'We use at with Christmas and other holidays that last several days.',
    },
    {
      kind: 'matching',
      instruction: 'Match each preposition with the kind of time it goes with:',
      pairs: [
        { left: 'at', right: 'clock times' },
        { left: 'on', right: 'days and dates' },
        { left: 'in', right: 'months, seasons and years' },
      ],
    },
  ],

  'cardinal-numbers': [
    {
      kind: 'multiple-choice',
      prompt: 'Helena teaches a class of 15 students. In words, 15 is ___.',
      options: ['fifteen', 'fifty', 'fiveteen'],
      correctIndex: 0,
      explanation: 'Fifteen is five plus 10 — the suffix -teen comes from the word ten.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The suffix -teen means plus 10. So nineteen means ___.',
      options: ['nine plus ten', 'nine times ten', 'ninety'],
      correctIndex: 0,
      explanation: 'The suffix -teen comes from ten: nineteen is nine plus 10.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Sofia’s grandmother just turned 90. In words, 90 is ___.',
      options: ['ninety', 'nineteen', 'ninty'],
      correctIndex: 0,
      explanation: 'Tens end in -ty: twenty, thirty… ninety — don’t confuse it with nineteen (19).',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence follows the writing rule for numbers?',
      options: [
        'I have two siblings.',
        'I have 2 siblings.',
        'I bought 8 books for my sister.',
      ],
      correctIndex: 0,
      explanation: 'When writing, it’s best to spell out cardinal numbers one to nine; numerals are for large numbers.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Ana manages a team of 40 people. In words, 40 is ___.',
      answers: ['forty'],
      explanation: 'Careful with the spelling: four, fourteen, but forty — without the u.',
    },
    {
      kind: 'fill-blank',
      prompt: 'The workshop lasts 12 weeks. In words, 12 is ___.',
      answers: ['twelve'],
      explanation: 'Eleven and twelve have unique names — they don’t follow the -teen pattern.',
    },
    {
      kind: 'matching',
      instruction: 'Match each number with its word:',
      pairs: [
        { left: '13', right: 'thirteen' },
        { left: '30', right: 'thirty' },
        { left: '15', right: 'fifteen' },
        { left: '50', right: 'fifty' },
      ],
      explanation: '-teen means plus 10 (thirteen = 13); -ty marks the tens (thirty = 30).',
    },
  ],

  'ordinal-numbers-dates': [
    {
      kind: 'multiple-choice',
      prompt: 'Larissa came ___ in the national exam — number 1!',
      options: ['first', 'oneth', 'one'],
      correctIndex: 0,
      explanation: 'First, second, and third are the only exceptions — they don’t take the -th suffix.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Her office is on the ___ floor. (5)',
      options: ['fifth', 'fiveth', 'five'],
      correctIndex: 0,
      explanation: 'Ordinal numbers are used to refer to a floor of a building: the fifth floor, pronounced /fith/.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The museum was established in the 18th century. How do we say 18th?',
      options: ['eighteenth', 'eighteen', 'eightieth'],
      correctIndex: 0,
      explanation: 'We use ordinal numbers to refer to centuries: 18th = eighteenth (80th is eightieth).',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which three ordinal numbers are the exceptions that don’t end in -th?',
      options: [
        'first, second, third',
        'fourth, fifth, sixth',
        'ninth, twelfth, twentieth',
      ],
      correctIndex: 0,
      explanation: 'Most ordinals add -th to the cardinal number; the only exceptions are first, second, and third.',
    },
    {
      kind: 'fill-blank',
      prompt: 'My birthday is on the ___ of next month. (26)',
      answers: ['26th', 'twenty-sixth', 'twenty sixth'],
      explanation: 'We use ordinal numbers to give a date in the calendar: the 26th.',
    },
    {
      kind: 'fill-blank',
      prompt: '3 → third; 12 → ___.',
      answers: ['twelfth', '12th'],
      explanation: 'Twelve becomes twelfth, pronounced /twelth/ — watch the spelling change.',
    },
    {
      kind: 'matching',
      instruction: 'Match each number with its ordinal word:',
      pairs: [
        { left: '1st', right: 'first' },
        { left: '2nd', right: 'second' },
        { left: '3rd', right: 'third' },
        { left: '20th', right: 'twentieth' },
        { left: '40th', right: 'fortieth' },
      ],
      explanation: 'Ordinal numbers signify order and rank: most just add -th to the cardinal number.',
    },
  ],
};
